// src/components/MallReportsTab.js
// Mall admin "Reports" tab — daily collection across all stalls, online vs cash, per-vendor breakdown + CSV.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DateRangePicker from './DateRangePicker';
import { downloadCSV } from '../utils/downloadCSV';

function toDateStr(d) { return d.toISOString().slice(0, 10); }
function defaultRange() {
  const end = new Date();
  const start = new Date(end); start.setDate(start.getDate() - 29);
  return { startDate: toDateStr(start), endDate: toDateStr(end) };
}
const cardStyle = { background: 'var(--bg2)', borderRadius: 10, padding: 14, minWidth: 140 };

export default function MallReportsTab({ mallId, vendors = [] }) {
  const [range, setRange]       = useState(defaultRange());
  const [vendorFilter, setVendorFilter] = useState('');
  const [report, setReport]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const load = useCallback(async (r, vId) => {
    if (!mallId) return;
    setLoading(true); setError('');
    try {
      const params = { ...r };
      if (vId) params.vendorId = vId;
      const res = await api.get(`/mall/${mallId}/reports/daily`, { params });
      setReport(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [mallId]);

  useEffect(() => { load(range, vendorFilter); }, [load, range, vendorFilter]);

  const totals = report?.totals;
  const csvParams = { ...range, ...(vendorFilter ? { vendorId: vendorFilter } : {}) };

  return (
    <div className="card">
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>📊 Food Court Daily Collection Report</div>
      <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
        Online vs cash breakdown across all stalls in this mall.
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <DateRangePicker startDate={range.startDate} endDate={range.endDate} onChange={setRange} />
        <select className="input" style={{ width: 200 }} value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)}>
          <option value="">All vendors</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button className="btn btn-sm" onClick={() => downloadCSV(`/mall/${mallId}/reports/daily/csv`, csvParams, `daily-report-${range.startDate}-to-${range.endDate}.csv`)}>
          📥 Daily CSV
        </button>
        <button className="btn btn-sm" onClick={() => downloadCSV(`/mall/${mallId}/reports/vendor-summary/csv`, csvParams, `vendor-summary-${range.startDate}-to-${range.endDate}.csv`)}>
          📥 Vendor Summary CSV
        </button>
      </div>

      {loading && <div style={{ color: 'var(--text2)' }}>Loading…</div>}
      {error && <div style={{ color: '#e55' }}>{error}</div>}

      {totals && !loading && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
            <div style={cardStyle}>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>Total</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>AED {totals.combined.total.toFixed(2)}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>{totals.combined.orders} orders</div>
            </div>
            <div style={cardStyle}>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>Online</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>AED {totals.online.total.toFixed(2)}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>{totals.online.orders} orders</div>
            </div>
            <div style={cardStyle}>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>Cash</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>AED {totals.cash.total.toFixed(2)}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>{totals.cash.orders} orders</div>
            </div>
            <div style={cardStyle}>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>Commission</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>AED {totals.totalCommission.toFixed(2)}</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
            <div style={cardStyle}>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>Service Fees</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>AED {totals.totalServiceFees.toFixed(2)}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>Plat: {totals.platformServiceFeeShare.toFixed(2)} · Mall: {totals.mallServiceFeeShare.toFixed(2)}</div>
            </div>
            <div style={cardStyle}>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>Net to Vendors</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>AED {totals.totalNetPayable.toFixed(2)}</div>
            </div>
          </div>

          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Per-Vendor Breakdown</div>
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Online Ord</th><th>Online Amt</th>
                  <th>Cash Ord</th><th>Cash Amt</th>
                  <th>Total</th><th>Net</th>
                </tr>
              </thead>
              <tbody>
                {report.vendorBreakdown.map(v => (
                  <tr key={v.vendorId}>
                    <td>{v.vendorName}</td>
                    <td>{v.online.orders}</td><td>{v.online.total.toFixed(2)}</td>
                    <td>{v.cash.orders}</td><td>{v.cash.total.toFixed(2)}</td>
                    <td>{v.combined.total.toFixed(2)}</td>
                    <td>{v.netPayable.toFixed(2)}</td>
                  </tr>
                ))}
                {report.vendorBreakdown.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text2)' }}>No paid orders in this period</td></tr>
                )}
              </tbody>
              {report.vendorBreakdown.length > 0 && (
                <tfoot>
                  <tr style={{ fontWeight: 700 }}>
                    <td>Totals</td>
                    <td>{totals.online.orders}</td><td>{totals.online.total.toFixed(2)}</td>
                    <td>{totals.cash.orders}</td><td>{totals.cash.total.toFixed(2)}</td>
                    <td>{totals.combined.total.toFixed(2)}</td>
                    <td>{totals.totalNetPayable.toFixed(2)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
}
